import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

/**
 * Keeps the active tab in sync with the URL hash, so tabs are bookmarkable and reload-safe.
 *
 * @param defaultTab tab shown when the URL has no usable hash.
 * @param validTabs when given, an unknown hash is ignored instead of rendering an empty tab pane.
 * @param keepSearch keep the query string on tab change. `history.replace` drops it by default —
 *   pass `true` for a page whose query params (e.g. `?valid_at=`) must survive a tab switch.
 */
export default function useActiveTab(defaultTab = 'acquisition', validTabs?: readonly string[], keepSearch = false) {
  const [activeTab, setActiveTab] = useState<string>(defaultTab)

  const { hash } = useLocation()
  useEffect(() => {
    const tab = hash?.replace('#', '')
    if (tab && (!validTabs || validTabs.includes(tab))) {
      setActiveTab(tab)
    }
  }, [hash, validTabs])

  const history = useHistory()
  const handleOnTabChange = useCallback(
    (activeKey: string) => {
      setActiveTab(activeKey)
      // `replace` builds a fresh location, so the query string is dropped unless carried over explicitly.
      history.replace({
        search: keepSearch ? history.location?.search : '',
        hash: activeKey,
      })
    },
    [history, keepSearch]
  )

  return { activeTab, handleOnTabChange }
}
